import { useCallback, useState } from 'react'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { AppV2 } from './v2/AppV2'

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  )
}

function AppShell() {
  const { token, authLoading, error, login, logout } = useAuth()

  // PLAN-permalink-cockpit-convergence Phase C: /v/:id permalink はログイン強制しない。
  // 未認証でも AppV2 に通し、PermalinkView が public は表示・private は not-found を出す。
  const isPermalink = typeof window !== 'undefined' && window.location.pathname.startsWith('/v/')

  if (!token && !isPermalink) {
    return <LoginForm authLoading={authLoading} error={error} onLogin={login} />
  }

  return <AppV2 onUnauthorized={logout} />
}

interface LoginFormProps {
  authLoading: boolean
  error: string | null
  onLogin: (userId: string, password: string) => Promise<void>
}

function LoginForm({ authLoading, error, onLogin }: LoginFormProps) {
  const [userId, setUserId] = useState('')
  const [password, setPassword] = useState('')

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (authLoading) return
    await onLogin(userId, password)
  }, [authLoading, onLogin, userId, password])

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4"
      >
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-800">KDB Cockpit</h1>
          <p className="mt-1 text-xs text-slate-500">KDB の探索・俯瞰インターフェース</p>
        </div>
        <div className="space-y-3">
          <div>
            <label htmlFor="user_id" className="block text-xs text-slate-600 mb-1">ID</label>
            <input
              id="user_id"
              type="text"
              autoComplete="username"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={authLoading}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-slate-500 disabled:bg-slate-100"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-xs text-slate-600 mb-1">パスワード</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={authLoading}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-slate-500 disabled:bg-slate-100"
            />
          </div>
        </div>
        {error && (
          <p className="text-xs text-red-600" role="alert">{error}</p>
        )}
        <button
          type="submit"
          disabled={authLoading || !userId || !password}
          className="w-full px-4 py-2 bg-slate-800 text-white rounded hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {authLoading ? '認証中...' : 'ログイン'}
        </button>
      </form>
    </div>
  )
}
